import { css } from '@styled-system/css';
import { type FC, useState } from 'react';

import { useSettingsData } from '@/features/settings/hooks/useSettingsData';
import { CloseButton } from '@/shared/components/ui/CloseButton';
import { Logo } from '@/shared/components/ui/Logo';
import { MenuCloseButton } from '@/shared/components/ui/MenuCloseButton';
import { Separator } from '@/shared/components/ui/Separator';
import { useIsMobile } from '@/shared/hooks/useIsMobile';

import { AccountTab } from './AccountTab';
import { PaymentTab } from './PaymentTab';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabType = 'account' | 'payment';

const TAB_TITLE: Record<TabType, string> = {
  account: 'Compte',
  payment: 'Paiement',
};

export const SettingsModal: FC<SettingsModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabType>('account');
  const { settings } = useSettingsData();
  const isMobile = useIsMobile();

  if (!isOpen) return null;

  const backdropStyles = css({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    bg: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  });

  const modalStyles = css({
    bg: '#0f1012', // base-bg
    border: '0.75px solid',
    borderColor: 'base.border', // #1e1f24
    borderRadius: isMobile ? '0' : '0.625rem', // 10px
    w: isMobile ? '100%' : '40rem', // 640px
    h: isMobile ? '100%' : '33.0625rem', // 529px
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
  });

  // Styles pour positionner les boutons de fermeture
  const closeButtonPositionStyles = css({
    position: 'absolute',
    top: '0.5rem', // 8px
    right: '0.5rem', // 8px
    zIndex: 10,
  });

  const mobileCloseButtonPositionStyles = css({
    position: 'absolute',
    top: '35px',
    right: '18px',
    zIndex: 10,
  });

  // Sidebar à gauche avec les tabs (ou top bar sur mobile)
  const sidebarStyles = css({
    bg: 'base.segBg', // #16171b
    w: isMobile ? '100%' : '11.875rem', // 190px
    h: isMobile ? 'auto' : '100%',
    display: 'flex',
    flexDirection: isMobile ? 'row' : 'column',
    gap: '0.3125rem', // 5px
    p: isMobile ? '1rem' : '1.25rem 0.625rem', // 20px 10px
    flexShrink: 0,
  });

  const tabButtonStyles = css({
    display: 'flex',
    alignItems: 'center',
    gap: '0.3125rem', // 5px
    h: '1.8125rem', // 29px
    px: '0.375rem', // 6px
    py: '0.4375rem', // 7px
    bg: 'transparent',
    border: 'none',
    borderRadius: '0.375rem', // 6px
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    w: isMobile ? 'auto' : '100%',
    flex: isMobile ? 1 : 'none',
    textAlign: 'left',
    outline: 'none', // Enlever la bordure bleue du focus
    justifyContent: isMobile ? 'center' : 'flex-start',
    _hover: {
      bg: '#222328',
    },
    _focus: {
      outline: 'none', // Enlever la bordure bleue du focus
    },
  });

  const activeTabStyles = css({
    bg: '#222328', // selected-bg
  });

  const tabIconStyles = css({
    w: '1rem', // 16px
    h: '1rem', // 16px
    flexShrink: 0,
  });

  const tabLabelStyles = css({
    fontSize: '0.8125rem', // 13px
    fontWeight: 'medium',
    color: 'font.primary', // #e6e6e9
    fontFamily: 'Manrope, sans-serif',
    lineHeight: 1.07,
    whiteSpace: 'nowrap',
  });

  // Zone de contenu à droite
  const contentContainerStyles = css({
    flex: 1,
    h: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    gap: 'md',
  });

  const contentHeaderStyles = css({
    display: 'flex',
    flexDirection: 'column',
    gap: '0.7rem',
    p: '1.125rem 1.1875rem 0', // 18px 19px 0
    flexShrink: 0,
  });

  const headerTitleStyles = css({
    fontSize: '0.8125rem', // 13px
    fontWeight: 'medium',
    color: 'font.primary', // #e6e6e9
    fontFamily: 'Manrope, sans-serif',
    lineHeight: 1.07,
    position: 'relative',
    // '&::after': {
    //   content: '""',
    //   position: 'absolute',
    //   bottom: '-0.8125rem', // -13px
    //   left: 0,
    //   w: '25.6875rem', // 411px
    //   h: '1px',
    //   bg: 'base.segBg', // #16171b
    //   borderRadius: '99px',
    // },
  });

  const contentBodyStyles = css({
    flex: 1,
    overflow: 'auto',
    px: '1.1875rem', // 19px
    pb: '1.125rem', // 18px
  });

  // Gérer la fermeture avec clic sur backdrop
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className={backdropStyles} onClick={handleBackdropClick}>
      <div className={modalStyles}>
        {/* Bouton de fermeture */}
        {isMobile ? (
          <MenuCloseButton onClick={onClose} className={mobileCloseButtonPositionStyles} />
        ) : (
          <CloseButton onClick={onClose} className={closeButtonPositionStyles} />
        )}

        {/* Sidebar avec les tabs */}
        <div className={sidebarStyles}>
          <button
            className={`${tabButtonStyles} ${activeTab === 'account' ? activeTabStyles : ''}`}
            onClick={() => setActiveTab('account')}
          >
            <Logo variant="account" className={tabIconStyles} color="font.primary" />
            <span className={tabLabelStyles}>{TAB_TITLE['account']}</span>
          </button>
          <button
            className={`${tabButtonStyles} ${activeTab === 'payment' ? activeTabStyles : ''}`}
            onClick={() => settings.isPaymentTabAvailable && setActiveTab('payment')}
            disabled={!settings.isPaymentTabAvailable}
            style={!settings.isPaymentTabAvailable ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
          >
            <Logo variant="payment" className={tabIconStyles} color="font.primary" />
            <span className={tabLabelStyles}>{TAB_TITLE['payment']}</span>
          </button>
        </div>

        {/* Zone de contenu */}
        <div className={contentContainerStyles}>
          <div className={contentHeaderStyles}>
            <h2 className={headerTitleStyles}>{TAB_TITLE[activeTab]}</h2>
            <Separator />
          </div>
          <div className={contentBodyStyles}>
            {activeTab === 'account' && <AccountTab />}
            {activeTab === 'payment' && settings.isPaymentTabAvailable && <PaymentTab />}
          </div>
        </div>
      </div>
    </div>
  );
};
